import { TabContext, TabList } from "@mui/lab";
import {
  Box,
  Button,
  Divider,
  Grid,
  MenuItem,
  Select,
  Tab,
  Typography,
} from "@mui/material";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { LoadingButton } from "src/grading/components";
import AthletesLayout from "src/grading/layout/AthletesLayout/AthletesLayout";
import { DEFAULT_ORGANIZATION } from "src/grading/models";
import { useAppSelector } from "src/store/hooks";
import { selectCurrentUser } from "src/store/reducers/auth/authSlice";
import { useGetOrganizationsQuery } from "src/store/reducers/organizations/organizationsApiSlice";
import {
  ReportUser,
  useExportUsersReportMutation,
  useGetUsersReportMutation,
} from "src/store/reducers/reporting/reportingApiSlice";
import theme from "src/theme";
import AddNewAthleteModal from "../LiveTest/SelectAthlete/AddNewAthleteModal/AddNewAthleteModal";
import useStyles from "./Athletes.styles";
import AthleteFilters from "./AthletesFilters/AthleteFilters";
import { ReportingCheckboxGroup } from "./ReportingCheckboxGroup/ReportingCheckboxGroup";
import ReportingTable from "./ReportingTable/ReportingTable";

export const initialFilterValues = {
  search: "",
  sortBy: "-1",
};

enum TabValues {
  CommonColumns = "0",
  AllColumns = "1",
}

const COLUMNS = {
  COMMON: [
    { id: "u.id", label: "User ID", checked: false },
    { id: "u.subscriptionLevel", label: "Subscription", checked: true },
    { id: "u.firstName", label: "First name", checked: true },
    { id: "u.lastName", label: "Last name", checked: true },
    { id: "u.email", label: "Email", checked: true },
    { id: "u.activeTrainingPlanId", label: "Training", checked: true },
    { id: "u.lastTestId", label: "Test Completed", checked: true },
    { id: "u.createdAt", label: "Created at", checked: true },
    { id: "u.lastAppUse", label: "Last app use", checked: true },
  ],
  PERSONAL: [
    { id: "u.profileName", label: "Profile name", checked: false },
    { id: "u.branchOfService", label: "Branch of service", checked: false },
    { id: "u.athleteStatus", label: "Athlete status", checked: false },
    { id: "u.primaryTest", label: "Primary test", checked: false },
    { id: "u.gender", label: "Gender", checked: false },
    { id: "u.height", label: "Height", checked: false },
    { id: "u.weight", label: "Weight", checked: false },
    { id: "u.bmi", label: "BMI", checked: false },
    { id: "u.neckCircumference", label: "Neck circumference", checked: false },
    {
      id: "u.abdomenCircumference",
      label: "Abdomen circumference",
      checked: false,
    },
    { id: "u.hipCircumference", label: "Hip circumference", checked: false },
    {
      id: "u.waistCircumference",
      label: "Waist circumference",
      checked: false,
    },
    { id: "u.bodyFatPercentage", label: "Body fat percentage", checked: false },
    { id: "u.address", label: "Address", checked: false },
    { id: "u.timezone", label: "Timezone", checked: false },
  ],
  ORGANIZATION: [
    { id: "u.organizationId", label: "Organization ID", checked: false },
    { id: "o.name", label: "Organization name", checked: false },
  ],
  BUSINESS: [
    { id: "u.pricePaid", label: "Price paid", checked: false },
    {
      id: "u.subscriptionStartDate",
      label: "Subscription start date",
      checked: false,
    },
    {
      id: "u.subscriptionEndDate",
      label: "Subscription end date",
      checked: false,
    },
  ],
  TEST: [
    { id: "t.date", label: "Last test date", checked: false },
    { id: "t.title", label: "Test title", checked: false },
    { id: "t.type", label: "Test type", checked: false },
    { id: "t.score", label: "Test score", checked: false },
    { id: "t.status", label: "Test status", checked: false },
    { id: "t.level", label: "Test level", checked: false },
  ],
  TEST_EXERCISE: [
    { id: "te.name", label: "Exercise name", checked: false },
    { id: "te.rawScore", label: "Exercise raw score", checked: false },
    {
      id: "te.calculatedScore",
      label: "Exercise calculated score",
      checked: false,
    },
  ],
  TRAINING_PLAN: [
    { id: "p.name", label: "Plan name", checked: false },
    { id: "c.firstName", label: "Coach first name", checked: false },
    { id: "c.lastName", label: "Coach last name", checked: false },
    { id: "w.date", label: "Last workout date", checked: false },
    { id: "p.workoutsCompleted", label: "Workouts completed", checked: false },
    { id: "p.workoutsRemaining", label: "Workouts remaining", checked: false },
    {
      id: "u.totalWorkoutsCompleted",
      label: "Total workouts completed",
      checked: false,
    },
  ],
  WORKOUT: [
    { id: "w.name", label: "Workout name", checked: false },
    { id: "w.status", label: "Workout status", checked: false },
  ],
};

const ROWS_PER_PAGE = 15;

const Athletes = () => {
  const classes = useStyles();
  const [reportUsers, setReportUsers] = useState<ReportUser[]>([]);
  const user = useAppSelector(selectCurrentUser);
  const [newAthleteModalOpen, setNewAthleteModalOpen] = useState(false);

  const [getUsersReport, { isLoading: usersReportLoading }] =
    useGetUsersReportMutation();

  const [exportUsersReport, { isLoading: isExporting }] =
    useExportUsersReportMutation();

  const { data: organizationsData } = useGetOrganizationsQuery();
  const [organizationId, setOrganizationId] = useState(
    user.organizationId ? user.organizationId : DEFAULT_ORGANIZATION.id,
  );

  const [commonColumns, setCommonColumns] = useState(COLUMNS.COMMON);
  const [personalColumns, setPersonalColumns] = useState(COLUMNS.PERSONAL);
  const [organizationColumns, setOrganizationColumns] = useState(
    COLUMNS.ORGANIZATION,
  );
  const [businessColumns, setBusinessColumns] = useState(COLUMNS.BUSINESS);
  const [testColumns, setTestColumns] = useState([
    ...COLUMNS.TEST,
    ...COLUMNS.TEST_EXERCISE,
  ]);
  const [trainingColumns, setTrainingColumns] = useState([
    ...COLUMNS.TRAINING_PLAN,
    ...COLUMNS.WORKOUT,
  ]);

  const [tabValue, setTabValue] = useState(TabValues.CommonColumns);

  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);

  const handleExportAthletes = async () => {
    let url = "";
    try {
      const columns: { [key: string]: boolean } = {};
      [
        ...commonColumns,
        ...personalColumns,
        ...organizationColumns,
        ...businessColumns,
        ...testColumns,
        ...trainingColumns,
      ].forEach((c) => (columns[c.id] = c.checked));

      const filters: { search?: string; sortBy?: string } = {};
      if (values.search) filters.search = values.search;
      if (values.sortBy !== "-1") filters.sortBy = values.sortBy;

      url = await exportUsersReport({
        ...filters,
        ...columns,
      }).unwrap();
    } catch (error) {
      console.log(error);
    }

    const a = document.createElement("a");
    a.style.display = "none";
    a.href = url;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const fetchReportUsers = async ({ page }: { page: number }) => {
    const columns: { [key: string]: boolean } = {};
    [
      ...commonColumns,
      ...personalColumns,
      ...organizationColumns,
      ...businessColumns,
      ...testColumns,
      ...trainingColumns,
    ].forEach((c) => (columns[c.id] = c.checked));

    const filters: { search?: string; sortBy?: string } = {};
    if (values.search) filters.search = values.search;
    if (values.sortBy !== "-1") filters.sortBy = values.sortBy;

    const report = await getUsersReport({
      limit: ROWS_PER_PAGE,
      offset: page * ROWS_PER_PAGE,
      ...filters,
      ...columns,
    }).unwrap();
    if (report?.users.length > 0) {
      setReportUsers(report.users);
    }

    setTotal(report.total);
  };

  const handleUpdateReport = async () => {
    setPage(0);
    await fetchReportUsers({ page: 0 });
  };

  const handleChangePage = async (event: unknown, newPage: number) => {
    setPage(newPage);
    await fetchReportUsers({ page: newPage });
  };

  useEffect(() => {
    const fetchUsers = async () => {
      const report = await getUsersReport({ limit: 15, offset: 0 }).unwrap();
      if (report?.users.length > 0) {
        setReportUsers(report.users);
      }
      setTotal(report.total);
    };

    fetchUsers();
  }, [getUsersReport]);

  const formik = useFormik({
    initialValues: initialFilterValues,
    onSubmit: async (values) => {},
  });

  const { values, setFieldValue, errors, touched } = formik;

  return (
    <AthletesLayout>
      <Grid container className={classes.container}>
        {/* ATHLETES HEADER CONTAINER */}
        <Grid container item alignItems={"center"}>
          <Grid item>
            <Grid container alignItems={"center"}>
              <Typography variant="h2">Athletes</Typography>
              <Typography variant="caption" className={classes.usersLength}>
                {total}
              </Typography>
              {organizationsData?.organizations &&
              organizationsData.organizations.length > 1 ? (
                <Select
                  variant="outlined"
                  className={classes.select}
                  value={organizationId}
                  onChange={(e) => setOrganizationId(e.target.value)}
                >
                  {organizationsData?.organizations?.map((o) => (
                    <MenuItem key={o.id} value={o.id}>
                      {o.name}
                    </MenuItem>
                  ))}
                </Select>
              ) : (
                <Typography variant="caption" className={classes.usersLength}>
                  {user.organizationId
                    ? user?.organization?.name
                    : DEFAULT_ORGANIZATION.name}
                </Typography>
              )}
            </Grid>
          </Grid>

          <Grid item className={classes.newAthleteButtonContainer}>
            <LoadingButton
              variant="contained"
              color="primary"
              label="Export Athletes"
              loading={isExporting}
              style={{
                marginRight: theme.spacing(2),
                width: theme.spacing(21),
              }}
              onClick={handleExportAthletes}
            />

            <Button
              variant="contained"
              color="primary"
              onClick={() => setNewAthleteModalOpen(true)}
            >
              New Athlete
            </Button>
          </Grid>
        </Grid>

        {/* FILTERS */}
        <AthleteFilters
          values={values}
          setFieldValue={setFieldValue}
          debounceSearch={() => {}}
          filterSearch={() => {}}
          errors={errors}
          touched={touched}
          units={[]}
        />

        {/* TABS */}
        <Grid item container xs={12} mt={5}>
          <Box className={classes.tabsContainer}>
            <TabContext value={tabValue}>
              <Box>
                <TabList
                  onChange={(event, newValue) => setTabValue(newValue)}
                  TabIndicatorProps={{
                    style: { color: "white", height: "100%" },
                    className: classes.tabIndicator,
                  }}
                >
                  <Tab
                    title="Pick From Common Columns"
                    value={TabValues.CommonColumns}
                    label={"Pick From Common Columns"}
                    className={classes.tab}
                  />
                  <Tab
                    title="Pick From All Columns"
                    value={TabValues.AllColumns}
                    label={"Pick From All Columns"}
                    className={classes.tab}
                  />
                </TabList>
              </Box>
            </TabContext>
          </Box>
        </Grid>

        <Grid
          item
          container
          mt={1}
          xs={12}
          className={classes.tabControlsContainer}
        >
          <ReportingCheckboxGroup
            title="Common"
            columns={commonColumns}
            setColumns={setCommonColumns}
          />
          {tabValue === TabValues.AllColumns && (
            <>
              <ReportingCheckboxGroup
                title="Personal"
                columns={personalColumns}
                setColumns={setPersonalColumns}
              />
              <ReportingCheckboxGroup
                title="Organization"
                columns={organizationColumns}
                setColumns={setOrganizationColumns}
              />
              <ReportingCheckboxGroup
                title="Business"
                columns={businessColumns}
                setColumns={setBusinessColumns}
              />
              <ReportingCheckboxGroup
                title="Tests"
                columns={testColumns}
                setColumns={setTestColumns}
              />
              <ReportingCheckboxGroup
                title="Training"
                columns={trainingColumns}
                setColumns={setTrainingColumns}
              />
            </>
          )}
          <Grid item container ml={theme.spacing(4)} mr={theme.spacing(4)}>
            <Divider style={{ width: "100%" }} />
            <LoadingButton
              variant="contained"
              color="primary"
              label="Update Report"
              loading={usersReportLoading}
              style={{
                marginTop: theme.spacing(2),
                marginRight: theme.spacing(2),
                width: theme.spacing(21),
              }}
              onClick={handleUpdateReport}
            />
          </Grid>
        </Grid>

        {/* Table */}
        <ReportingTable
          columns={[
            ...commonColumns,
            ...personalColumns,
            ...organizationColumns,
            ...businessColumns,
            ...testColumns,
            ...trainingColumns,
          ]}
          data={reportUsers}
          page={page}
          rowsPerPage={ROWS_PER_PAGE}
          total={total}
          handleChangePage={handleChangePage}
        />

        <AddNewAthleteModal
          onClose={() => setNewAthleteModalOpen(false)}
          open={newAthleteModalOpen}
        />
      </Grid>
    </AthletesLayout>
  );
};

export default Athletes;
